<template>
  <div class="content">
    <div class="title-text">
      <p class="title">Delete Artist</p>
      <p class="subtitle">
        Permanently delete this artist and all associated data.
      </p>
    </div>
    <div class="content-body" v-if="user.artists.length > 1">
      <div class="artist-card">
        <div class="card-content">
          <div class="artist-info">
            <div class="artist-image" v-if="artist.avatar_url">
              <img :src="artist.avatar_url" :alt="artist.name" />
            </div>
            <div class="artist-image-placeholder" v-else>
              {{ artist.name[0].toUpperCase() }}
            </div>
            <div class="artist-details">
              <div class="name">{{ artist.name }}</div>
              <div class="plan" v-if="artist.current_plan">
                {{ artist.current_plan.split("_")[0] | capitalize }} Plan
              </div>
            </div>
          </div>
          <div class="warning">
            <i class="fa-light fa-triangle-exclamation fa-lg"></i>
            <p>
              This action cannot be undone. This will permanently delete the
              artist and all associated data including links, statistics, and
              settings.
            </p>
          </div>
          <div class="actions">
            <button
              @click="confirmDelete"
              class="button-text danger"
              :disabled="submitting"
            >
              <span v-if="!submitting">Delete Artist</span>
              <span v-else>Deleting...</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="content-body" v-else>
      <div class="artist-card">
        <div class="card-content">
          <div class="warning">
            <i class="fa-light fa-info-circle fa-lg"></i>
            <p>
              You cannot delete your only artist. Add another artist first if
              you want to delete this one.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { M_DELETE_ARTIST, S_ARTISTS } from "@/graphql";
import Swal from "sweetalert2";

export default {
  name: "DeleteArtist",
  data() {
    return {
      submitting: false
    };
  },
  computed: {
    artist() {
      return this.$store.getters.getArtist;
    },
    user() {
      return this.$store.getters.getUser;
    }
  },
  filters: {
    capitalize(value) {
      if (!value) return "";
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
  },
  methods: {
    async confirmDelete() {
      const confirmDelete = await Swal.fire({
        title: "Delete this artist?",
        html: `<p class="description">You are about to delete <b style="color: #C5C5FF">${this.artist.name}</b>.</p>
              <p class="description">This action cannot be undone and will delete all associated data.</p>`,
        type: "warning",
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: "<span>Yes, delete artist</span>",
        cancelButtonText: "<span>No, keep artist</span>",
        confirmButtonClass: "button-danger big",
        cancelButtonClass: "button-secondary big",
        buttonsStyling: false,
        reverseButtons: true
      });

      if (!confirmDelete.value) {
        await this.$store.dispatch("snack", {
          text: "Cancelled! Your artist is safe."
        });
        return;
      }

      try {
        this.submitting = true;

        const nextArtist = this.user.artists.find(a => a.id !== this.artist.id);

        const response = await this.$apollo.mutate({
          client: "localClient",
          mutation: M_DELETE_ARTIST,
          variables: {
            artist_id: this.artist.id
          }
        });

        if (response.data.delete_artist.success) {
          const updatedArtists = this.user.artists.filter(
            a => a.id !== this.artist.id
          );
          await this.$store.dispatch("setUser", {
            ...this.user,
            artists: updatedArtists
          });

          await this.$store.dispatch("setArtist", nextArtist);

          await this.$store.dispatch("snack", {
            text: "Artist successfully deleted"
          });

          await this.$router.push({ name: "SmartLinks" });
        }
      } catch (err) {
        console.error(err);
        await this.$store.dispatch("snack", {
          type: "error",
          text: `Oops! ${err.message.replace("GraphQL error: ", "")}`
        });
      } finally {
        this.submitting = false;
      }
    }
  },
  apollo: {
    $subscribe: {
      artists: {
        query: S_ARTISTS,
        variables() {
          return {
            user_id: this.$store.getters.getUser.id
          };
        },
        result({ data }) {
          this.$store.dispatch("setUser", {
            ...this.user,
            artists: data.artists
          });
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.content-body {
  margin-top: 36px;
}

.artist-card {
  background: #24224a;
  border-radius: 10px;
  padding: 24px;

  .card-content {
    display: grid;
    grid-gap: 24px;
  }
}

.artist-info {
  display: flex;
  align-items: center;

  .artist-image {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    overflow: hidden;
    margin-right: 16px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .artist-image-placeholder {
    width: 60px;
    height: 60px;
    background: $purple-dark;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 16px;
    color: white;
    font-size: 24px;
    font-weight: bold;
  }

  .artist-details {
    .name {
      font-size: 18px;
      font-weight: 600;
      color: white;
      margin-bottom: 4px;
    }

    .plan {
      font-size: 14px;
      color: $text-type-0;
    }
  }
}

.warning {
  display: flex;
  align-items: center;
  padding: 16px;
  background: rgba(227, 80, 122, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(227, 80, 122, 0.2);

  i {
    color: #e3507a;
    margin-right: 12px;
    margin-top: 3px;
  }

  p {
    color: $text-type-0;
    font-size: 14px;
    line-height: 1.5;
    margin: 0;
  }
}

.actions {
  display: flex;
  justify-content: flex-end;

  button {
    border: 1px #e3507a solid;
    border-radius: 5px;

    &:focus,
    &:hover {
      color: #ffffff;
      border-color: #ffffff;
    }
  }
}
</style>
